/**
 * Page manager section features
 * @module services/pm-section
 */
import { List, Map } from 'immutable'
import { shouldPmScreenRefetch } from '../pm-screen/index'
import { ROW_IMPRESSION_GA4_EVENT } from '../event-tracking/index'
import { EN } from '../languages/constants'
import { getSafeNodeId } from '../node/index'
import {
  URL_PLAYLIST_WATCH_HISTORY,
  URL_ACCOUNT_SETTINGS,
  URL_PLAYLIST_WATCH_LATER,
  URL_PLAYLIST_FAVORITES,
  URL_RECENTLY_ADDED,
} from '../url/constants'

/** Non-tiles sections */
export const TYPE_PLACEMENT = 'placement'
export const TYPE_PROMOTION = 'promotion'
export const TYPE_GAIA_ORACLE = 'ai-chat-promotion'
/** Tiles sections */
export const TYPE_CONTENT_LIST = 'content-list'
export const TYPE_CONTENT_REFERENCES_LIST = 'content-references-list'
export const TYPE_CONTINUE_WATCHING = 'continue-watching'
export const TYPE_CONTINUE_WATCHING_ONLY = 'continue-watching-only'
export const TYPE_CONTINUE_WATCHING_V3 = 'continue-watching-v3'
export const TYPE_WATCH_AGAIN = 'watch-again'
export const TYPE_RECOMMENDED_CONTENT = 'recommended-content'
export const TYPE_MY_SERIES = 'my-series'
export const TYPE_RECENTLY_ADDED = 'recently-added'
export const TYPE_PLAYLIST = 'playlist'
export const TYPE_EXPLORE_BY_TOPIC = 'explore-by-topic'
export const TYPE_FEATURED_VIDEOS = 'featured-videos'
export const TYPE_RECOMMENDED_SERIES = 'recommended-series'
export const TYPE_SERIES_FOLLOWING = 'followed-series'
export const TYPE_BECAUSE_YOU_WATCHED = 'because-you-watched'
export const TYPE_MOST_POPULAR = 'most-popular' // also called Top 10
/** Deprecated yoga specific sections, excluded from SectionManager
 * can be removed when removing the old sections once SectionTilesList is released */
export const TYPE_PRACTICE_PICKER = 'practice-picker'
export const TYPE_RECOMMENDED_PRACTICES = 'recommended-practices'
/** Yoga home page specific sections */
export const TYPE_HEADER = 'header'
export const TYPE_PEOPLE_LIST = 'people-list'
export const TYPE_LINK = 'link'
export const TYPE_ARTICLE_LIST = 'article-list'
export const TYPE_PLAN_SELECTION = 'plan-selection'
export const TYPE_EMAIL_SIGNUP = 'email-signup'
/** Portals section */
export const TYPE_PORTALS = 'portals'
/** Member home first-touch overlay (mobile PoC); app.type from Page Manager */
export const TYPE_MEMBER_HOME_FIRST_TOUCH = 'member-home-first-touch'

export const MAX_ROW_ITEMS = 12
export const UI_LOCATION_MEMBER_HOME = 'Member Home'

export const DISPLAY_MODE_TILE_LIST_5X7 = 'tile-list-5x7'
export const DISPLAY_MODE_TILE_LIST_16X9 = 'tile-list-16x9'
export const DISPLAY_MODE_TILE_LIST_3X4 = 'tile-list-3x4'
export const DISPLAY_MODE_TILE_LIST_5X7_GRAY_BACKGROUND = 'tile-list-5x7-with-gray-background'
export const DISPLAY_MODE_TILE_LIST_16X9_GRAY_BACKGROUND = 'tile-list-16x9-with-gray-background'
export const DISPLAY_MODE_TILE_LIST_3X4_GRAY_BACKGROUND = 'tile-list-3x4-with-gray-background'
export const DISPLAY_MODE_TILE_LIST_16X9_BLUE_GRADIENT_BACKGROUND = 'tile-list-16x9-with-blue-gradient-background'
export const DISPLAY_MODE_TILE_LIST_5X7_BLUE_GRADIENT_BACKGROUND = 'tile-list-5x7-with-blue-gradient-background'
export const DISPLAY_MODE_TILE_LIST_3X4_BLUE_GRADIENT_BACKGROUND = 'tile-list-3x4-with-blue-gradient-background'
export const DISPLAY_MODE_TILE_LIST_TOP_TEN = 'tile-list-top-ten'
export const DISPLAY_MODE_TILE_LIST_TOP_TEN_3X4 = 'tile-list-top-ten-3x4'
export const DISPLAY_MODE_TILE_LIST_GAIA_PLUS = 'tile-list-gaia-plus'
export const DISPLAY_MODE_COURSES = 'courses'

export const DISPLAY_MODE_DEFAULT = DISPLAY_MODE_TILE_LIST_16X9

export const SECTIONS_CONFIG = {
  [TYPE_CONTENT_LIST]: {
    contextType: 'pm/my-content-list-list',
  },
  [TYPE_CONTENT_REFERENCES_LIST]: {
    contextType: 'pm/my-content-references-list',
  },
  [TYPE_CONTINUE_WATCHING]: {
    contextType: 'pm/continue-watching-list',
    contextLabel: 'continue watching',
    titleTextKey: 'continueWatchingTitle',
    viewAll: [{
      contextType: 'pm/continue-watching-section',
      url: URL_PLAYLIST_WATCH_HISTORY,
      linkTextKey: 'continueWatchingLinkText',
    }],
  },
  [TYPE_CONTINUE_WATCHING_ONLY]: {
    contextType: 'pm/continue-watching-list',
    contextLabel: 'continue watching',
    viewAll: [{
      url: URL_PLAYLIST_WATCH_HISTORY,
      linkTextKey: 'continueWatchingLinkText',
    }],
  },
  [TYPE_WATCH_AGAIN]: {
    contextType: 'pm/watch-again-list',
    contextLabel: 'watch again',
    viewAll: [{
      url: URL_PLAYLIST_WATCH_HISTORY,
      linkTextKey: 'continueWatchingLinkText',
    }],
  },
  [TYPE_FEATURED_VIDEOS]: {
    contextType: 'pm/featured-videos-list',
    contextLabel: 'popular',
  },
  [TYPE_MY_SERIES]: {
    contextType: 'pm/followed-series-list',
    contextLabel: 'followed series',
    titleTextKey: 'mySeriesTitle',
    viewAll: [{
      url: URL_ACCOUNT_SETTINGS,
      linkTextKey: 'mySeriesLinkText',
    }],
  },
  [TYPE_SERIES_FOLLOWING]: {
    contextType: 'pm/followed-series-list',
    contextLabel: 'followed series',
    titleTextKey: 'followedSeriesTitle',
    viewAll: [{
      url: URL_ACCOUNT_SETTINGS,
      linkTextKey: 'followedSeriesLinkText',
    }],
  },
  [TYPE_PLAYLIST]: {
    contextType: 'pm/my-playlist-list',
    contextLabel: 'playlist',
    titleTextKey: 'playlistTitle',
    viewAll: [{
      contextType: 'pm/my-playlist-section',
      url: URL_PLAYLIST_WATCH_LATER,
      linkTextKey: 'playlistLinkText',
    }, {
      contextType: 'pm/my-playlist-section',
      url: URL_PLAYLIST_FAVORITES,
      linkTextKey: 'playlistLinkText',
    }],
  },
  [TYPE_RECENTLY_ADDED]: {
    contextType: 'pm/my-recently-added-list',
    contextLabel: 'recently added',
    titleTextKey: 'recentlyAddedTitle',
    viewAll: [{
      contextType: 'pm/my-recently-added-section',
      url: URL_RECENTLY_ADDED,
      linkTextKey: 'recentlyAddedLinkText',
    }],
  },
  [TYPE_RECOMMENDED_CONTENT]: {
    contextType: 'pm/my-recommended-content-list',
    contextLabel: 'recommended content',
    titleTextKey: 'recommendedContentTitle',
  },
  [TYPE_RECOMMENDED_SERIES]: {
    contextType: 'pm/recommended-series-list',
    contextLabel: 'recommended series',
    titleTextKey: 'recommendedSeriesTitle',
    viewAll: [{
      url: '/all-series/series',
      linkTextKey: 'recommendedSeriesLinkText',
    }],
  },
  [TYPE_BECAUSE_YOU_WATCHED]: {
    contextType: 'pm/my-because-you-watched-section',
  },
  [TYPE_MOST_POPULAR]: {
    contextType: 'pm/my-top-10',
  },
}

/**
 * Determines whether the section should be shifted to the top
 * @param {String} prevSectionType The type of the section at the top of the current one
 * @param {String} displayMode The disply mode othe current section
 * @param {Map} featureTracking The feature tracking data
 * @returns {Boolean} True when the section should be shifted
 */

export const isShifted = (prevSectionType, displayMode, featureTracking) => {
  if (displayMode === DISPLAY_MODE_TILE_LIST_GAIA_PLUS) {
    return false
  }

  if (prevSectionType === TYPE_PLACEMENT && !featureTracking.getIn(['data', 'mhSpotlightCollapsed'])) {
    return true
  }

  return false
}

/**
 * The time to wait on error before getting a screen again
 */
export const GET_PM_SECTION_ERROR_CACHE_TIME = (300 * 1000)

/**
 * The time to wait on data before getting a screen again
 */
export const GET_PM_SECTION_DATE_CACHE_TIME = (60 * 1000)

/**
 * When the page manager section has data available returns true
 * @param {Object} options The options
 * @param {List} options.items The page manager section items have their data
 * @param {Map} options.videos The videos state
 * @param {Map} options.series The series state
 * @param {Map} options.term The term state
 * @param {String} options.language The language
 * @returns {Boolean} True when all the section items have data
 */
function pmSectionItemHasData (options = {}) {
  const {
    item = Map(), videos = Map(), series = Map(), term = Map(), language = EN,
  } = options
  const contentId = item.get('contentId')
  const contentType = item.get('contentType')
  if (!item) {
    return true
  }
  // If there is no content id and content type the data is loaded since there isn't any
  if (!contentId || !contentType) {
    return true
  }
  if (contentType === 'video' && videos) {
    return videos.hasIn([getSafeNodeId(contentId), language, 'data'])
  }
  if (contentType === 'series' && series) {
    return series.hasIn([getSafeNodeId(contentId), language, 'data'])
  }
  if (contentType === 'person' && term) {
    return term.hasIn([getSafeNodeId(contentId), language, 'data'])
  }
  if (contentType === 'tag' && term) {
    return term.hasIn([getSafeNodeId(contentId), language, 'data'])
  }
  return false
}

/**
 * When all the page manager section items have data or the items are empty returns true
 * @param {Object} options The options
 * @param {List} options.items The page manager section items
 * @param {Map} options.videos The videos state
 * @param {Map} options.series The series state
 * @param {Map} options.term The term state
 * @param {String} options.language The language
 * @returns {Boolean} True when all the section items have data or the items are empty
 */
export function pmSectionItemsHaveData (options = {}) {
  const {
    items = List(), videos = Map(), series = Map(), term = Map(), language = EN,
  } = options
  if (items.isEmpty()) {
    return true
  }
  return items.every((item) => pmSectionItemHasData({
    item, language, videos, series, term,
  }))
}

/**
 * When all the page manager section tabs have data or the items are empty returns true
 * @param {Object} options The options
 * @param {List} options.tabs The page manager section tabs
 * @param {Map} options.videos The videos state
 * @param {Map} options.series The series state
 * @param {Map} options.term The term state
 * @param {String} options.language The language
 * @param {Number} options.tabContentMaxItems The maximum number of items per row to limit
 * the data checked for the tab content
 * @returns {Boolean} True when all the section items have data or the items are empty
 */
export function pmSectionTabsHaveData (options = {}) {
  const {
    tabs = List(),
    videos = Map(),
    series = Map(),
    term = Map(),
    language = EN,
    tabContentMaxItems = MAX_ROW_ITEMS,
  } = options
  if (tabs.isEmpty()) {
    return true
  }
  return tabs.every((tab) => pmSectionItemsHaveData({
    items: (tab.get('tabContent') || List()).slice(0, tabContentMaxItems),
    videos,
    series,
    term,
    language,
  }))
}

/**
 * Given page manager section state get the tabs
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Boolean} True when all the section items have data or the items are empty
 */
export function getPmSectionTabs (options = {}) {
  const { sectionId, pmSection = Map() } = options
  if (!sectionId || !Map.isMap(pmSection)) {
    return List()
  }
  // PMSection.app is stored as data, and app.data is stored as data.data
  // Tabs are in the union type data, so access data.data.tabs
  return pmSection.getIn([sectionId, 'data', 'data', 'tabs']) || List()
}

/**
 * Given page manager section state get the processing value
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Boolean|undefined} The processing value
 */
export function getPmSectionProcessing (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'processing'])
}

/**
 * Given page manager section state get the type
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {String|undefined} The processing value
 */
export function getPmSectionType (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'data', 'type'])
}

/**
 * Given page manager section state get the displayMode
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {String} displayMode
 */
export function getPmSectionDisplayMode (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'data', 'data', 'displayMode'])
}

/**
 * Given page manager section state get the data
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Map|undefined} The processing value
 */
export function getPmSectionData (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'data'])
}

/**
 * Given page manager section data created
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Date|undefined} The processing value
 */
export function getPmSectionDataCreated (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'dataCreated'])
}

/**
 * Given page manager section error value
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Error|undefined} The processing value
 */
export function getPmSectionError (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'error'])
}

/**
 * Given page manager section error created
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Date|undefined} The processing value
 */
export function getPmSectionErrorCreated (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'errorCreated'])
}

/**
 * Given page manager section state get the done value i.e.
 * when all dependacies are loaded
 * @param {Object} options The options
 * @param {List} options.pmSection The page manager section state
 * @param {Map} options.sectionId The section id
 * @returns {Boolean} The processing value
 */
export function getPmSectionDone (options = {}) {
  const { sectionId, pmSection = Map() } = options
  return pmSection.getIn([sectionId, 'done'])
}

export function getTitleForCustomRows (item, language, videos, series, terms) {
  const itemId = item.get('contentId')
  const itemType = item.get('contentType')
  let store
  switch (itemType) {
    case 'video':
      store = videos
      break
    case 'series':
      store = series
      break
    case 'person':
      store = terms
      break
    case 'tag':
      store = terms
      break
    default:
  }

  const title = store && store.getIn([getSafeNodeId(itemId), language, 'data', 'title'])
  return `${itemId} | ${title}`
}

export function sendRowImpressionGA4Event (options) {
  const {
    sectionIndex = null,
    rowPlacement = null,
    rowType,
    visibilityRules,
    setEventDefault,
    unsetIndexes,
    items = List(),
    adminTitle,
    language,
    videoStore,
    seriesStore,
    termsStore,
    visibleIndexes,
    uiLocation = UI_LOCATION_MEMBER_HOME,
    tilesV2GraphqlActive = false,
  } = options
  const countItem = []
  const tileInfos = unsetIndexes.map((index, i) => {
    const itemsList = List.isList(items) ? items : List()
    const item = itemsList.get(index) || Map()
    const itemInfo = tilesV2GraphqlActive
      ? `${item.get('contentId')} | ${item.get('title')}`
      : getTitleForCustomRows(item, language, videoStore, seriesStore, termsStore)
    let itemInfoImpression = itemInfo
    itemInfoImpression = itemInfoImpression.split(' | ')
    itemInfoImpression[0] = i
    itemInfoImpression = itemInfoImpression.join(' | ')
    countItem[i] = [itemInfoImpression]
    return itemInfo
  })

  const eventData = ROW_IMPRESSION_GA4_EVENT
    .mergeDeep({
      row_placement: rowPlacement,
      row_name: adminTitle,
      row_number: sectionIndex,
      row_type: rowType,
      visible_tiles: visibleIndexes,
      visibility_rules: visibilityRules,
      content_info_array: tileInfos.join(' | '),
      ui_location: uiLocation,
    })

  setEventDefault(eventData)
}

export function preventSectionDataLoad (options) {
  const { now, dataCreated } = options

  if (!now || !dataCreated) {
    return false
  }

  if (!now.getTime || !dataCreated.getTime) {
    return false
  }

  return now.getTime() < (dataCreated.getTime() + GET_PM_SECTION_DATE_CACHE_TIME)
}

/**
 * Member home spotlight: skip redundant fetchSection on mount when section cache is fresh,
 * or when pmScreen will refetch (spotlight is in the initial sections preload).
 *
 * @param {Object} options
 * @param {boolean} options.isMemberHomePage
 * @param {boolean} options.hasSectionData
 * @param {Date|undefined} options.dataCreated
 * @param {Map} [options.pmScreen]
 * @param {string} [options.pmScreenType]
 * @param {string} [options.language]
 * @param {Date} [options.now]
 * @returns {boolean}
 */
export function shouldSkipFetchSectionOnMount ({
  isMemberHomePage,
  hasSectionData,
  dataCreated,
  pmScreen,
  pmScreenType,
  language,
  now = new Date(),
}) {
  if (!isMemberHomePage) {
    return false
  }

  if (hasSectionData && preventSectionDataLoad({ now, dataCreated })) {
    return true
  }

  if (shouldPmScreenRefetch({
    pmScreen, screenType: pmScreenType, language, now,
  })) {
    return true
  }

  return false
}
